/**
 * @module middleware/workflows/SmartConversion/SmartConversionHandlers
 */
import isUndefined from 'lodash/isUndefined';
import { push } from 'react-router-redux';

import { openModal } from '../../../actions/Modal';
import { setTrackingEvents } from '../../../actions/SmartConversion';
import { StatusCodes } from '../../../constants/SmartConversion';
import ViewActions from '../../../constants/ViewActions';
import { generateCachebustNumber } from '../../../lib/ApplicationUtil';
import { parseTrackingEvents } from '../../../lib/EventUtils';
import { isListenerEligibleForSmartConversion } from '../../../lib/ListenerUtils';
import { request } from '../../../lib/NetworkUtils';
import { getUpgradePath } from '../../../lib/RouteUtils';
import { getSmartConversionHaymakerUrl } from '../../../lib/SmartConversionUtils';
import { addUrlParameters } from '../../../lib/StringUtils';
import Logger from '../../../tools/Logger';

const log = Logger.child({ component: 'actions/workflows/SmartConversion' });
const HAYMAKER_URL_ACTION_PLACEHOLDER = '__ACTION__';

const onCoachmarkClose = dispatch => {
  dispatch({
    type: ViewActions.CLOSE_MODAL,
  });
};

const loadUpsellCoachmark = (smartConversionData, dispatch) => {
  const {
    headerTitle: title,
    bodyText: body,
    actionButtonTitle: ctaText,
    imageContent: iconSvgBitmap,
    productOffered,
    imageUrl,
  } = smartConversionData;
  const modalConfig = {
    modalType: 'ModalUpsell',
    onClose: () => onCoachmarkClose(dispatch),
    title,
    body,
    ctaText,
    iconSvgBitmap,
    imageUrl,
    productOffered,
  };
  return dispatch(openModal(modalConfig));
};

const isValidData = response => {
  return response && response.data && response.data.data;
};

// Replace the action and cachebust values
const populateHaymakerValues = (action, url) => {
  if (!url) {
    return null;
  }

  const populatedUrl = url.replace(HAYMAKER_URL_ACTION_PLACEHOLDER, action);
  return populatedUrl.replace(/__CACHEBUST__/, generateCachebustNumber());
};

export const onUpsellAction = (action, { dispatch, getState }) => {
  if (!isListenerEligibleForSmartConversion(getState())) {
    return;
  }
  return new Promise(resolve => {
    const state = getState();
    const url = getSmartConversionHaymakerUrl(state);
    if (!url) {
      log.error('Smart Conversion: do not have a smart conversion url');
      return resolve(StatusCodes.EMPTY_UPSELL_DATA);
    }

    request({
      url: populateHaymakerValues(action.payload.event, url),
    })
      .then(response => {
        if (!isValidData(response)) {
          log.error('Smart Conversion: unrecognized response: ' + response);
          return resolve(StatusCodes.UNRECOGNIZED_RESPONSE);
        }
        // Valid data but no upsell to present
        if (response.data.data.length === 0 || isUndefined(response.data.data[0])) {
          return resolve(StatusCodes.EMPTY_UPSELL_DATA);
        }
        const smartConversionData = response.data.data[0];
        const trackingEvents = parseTrackingEvents(smartConversionData.trackingEvents);
        if (!trackingEvents) {
          log.error('Smart Conversion: unrecognized tracking events: ' + trackingEvents);
          return resolve(StatusCodes.UNRECOGNIZED_TRACKING_EVENTS);
        }
        return dispatch(setTrackingEvents(trackingEvents))
          .then(() => {
            return loadUpsellCoachmark(smartConversionData, dispatch);
          })
          .then(resolve);
      })
      .catch(error => {
        log.error('Smart Conversion: network error: ' + error);
        return resolve(StatusCodes.NETWORK_ERROR);
      });
  });
};

export const onUpsellEngagement = ({ payload: { product } }, { dispatch }) => {
  return new Promise((resolve, reject) => {
    const params = { sku: product };
    return dispatch(push(addUrlParameters(getUpgradePath(), params)))
      .then(resolve)
      .catch(error => {
        log.error('Smart Conversion: error on upsell engagement flow: ' + error);
        reject(error);
      });
  });
};
